import React, { useState, useEffect } from 'react';
import { reportingAPI, questionAPI } from '../services/api';
import toast from 'react-hot-toast';

const getApiErrorMessage = (error, fallback = 'Failed to generate report') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === 'string' ? item : item?.msg || JSON.stringify(item)))
      .join(', ');
  }
  return fallback;
};

const ReportGeneration = ({ candidateId, onClose, onReportGenerated }) => {
  const [reportType, setReportType] = useState('assessment');
  const [loading, setLoading] = useState(false);
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [formData, setFormData] = useState({
    assessment_ids: [],
    session_id: '',
    include_skills_analysis: true,
    include_performance_metrics: true,
    include_recommendations: true,
    include_benchmarking: false,
    include_question_analysis: true,
    include_response_patterns: true,
    include_communication_style: true,
    include_cultural_fit: false,
    include_overall_score: true,
    include_strengths_weaknesses: true,
    include_development_areas: true,
    include_career_recommendations: true,
    include_team_fit_analysis: false,
    target_role: '',
    target_company: ''
  });

  useEffect(() => {
    if (reportType === 'assessment' || reportType === 'impression') {
      fetchAvailableAssessments();
    }
    if (reportType === 'prescreening' || reportType === 'impression') {
      fetchAvailableSessions();
    }
  }, [reportType, candidateId]);

  // Auto-select the most recent session when sessions are loaded
  useEffect(() => {
    if ((reportType === 'prescreening' || reportType === 'impression') && availableSessions.length > 0 && !formData.session_id) {
      setFormData(prev => ({ ...prev, session_id: availableSessions[0].id }));
    }
  }, [availableSessions]);

  const fetchAvailableAssessments = async () => {
    try {
      const response = await questionAPI.getAssessmentHistory(candidateId);
      // Ensure we always have an array
      const assessments = Array.isArray(response.data) ? response.data : 
                         Array.isArray(response.data?.assessments) ? response.data.assessments :
                         Array.isArray(response.data?.data) ? response.data.data : [];
      
      // Filter to only show completed AND passed assessments (backend should already filter, but adding extra safety)
      const passedAssessments = assessments.filter(a => 
        (a.status === 'completed' || (a.completed_at !== null && a.completed_at !== undefined)) &&
        a.passed === true
      );
      
      setAvailableAssessments(passedAssessments);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      toast.error('Failed to load assessment history');
      setAvailableAssessments([]); // Ensure it's always an array
    }
  };

  const fetchAvailableSessions = async () => {
    try {
      // If candidateId is provided (admin mode), use it; otherwise use current user's sessions
      const response = await questionAPI.getPrescreeningSessions(candidateId);
      const sessions = Array.isArray(response.data) ? response.data : [];
      
      // Only include completed sessions for report generation
      const completedSessions = sessions.filter(s => s.sessionStatus === 'completed');
      setAvailableSessions(completedSessions);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      toast.error('Failed to load prescreening sessions');
      setAvailableSessions([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssessmentToggle = (assessmentId) => {
    setFormData(prev => ({
      ...prev,
      assessment_ids: prev.assessment_ids.includes(assessmentId)
        ? prev.assessment_ids.filter(id => id !== assessmentId)
        : [...prev.assessment_ids, assessmentId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Auto-select all completed assessments for assessment reports
    if (reportType === 'assessment') {
      if (availableAssessments.length === 0) {
        toast.error('No completed assessments found for this candidate');
        return;
      }
      formData.assessment_ids = availableAssessments.map(a => a.id || a.attempt_id || a.assessment_id);
    }
    
    // Auto-select the most recent completed session if no session is selected
    if ((reportType === 'prescreening' || reportType === 'impression') && !formData.session_id && availableSessions.length > 0) {
      formData.session_id = availableSessions[0].id;
    }

    if (reportType === 'prescreening' && availableSessions.length === 0) {
      toast.error('No completed prescreening found for this candidate. The candidate must complete prescreening before a report can be generated.');
      return;
    }

    try {
      setLoading(true);
      let response;

      switch (reportType) {
        case 'assessment':
          response = await reportingAPI.generateAssessmentReport(candidateId, {
            assessment_ids: formData.assessment_ids,
            include_skills_analysis: formData.include_skills_analysis,
            include_performance_metrics: formData.include_performance_metrics,
            include_recommendations: formData.include_recommendations,
            include_benchmarking: formData.include_benchmarking
          });
          
          // Handle multiple reports (one per assessment)
          if (Array.isArray(response.data)) {
            const reportCount = response.data.length;
            if (reportCount > 1) {
              toast.success(`${reportCount} separate reports started! One for each skill assessment.`);
            } else if (reportCount === 1) {
              toast.success('Report generation started!');
            }
          }
          break;
        
        case 'prescreening':
          response = await reportingAPI.generatePrescreeningReport(candidateId, {
            session_id: formData.session_id,
            include_question_analysis: formData.include_question_analysis,
            include_response_patterns: formData.include_response_patterns,
            include_communication_style: formData.include_communication_style,
            include_cultural_fit: formData.include_cultural_fit
          });
          break;
        
        case 'impression':
          response = await reportingAPI.generateImpressionReport(candidateId, {
            assessment_data: formData.assessment_ids.length > 0 ? {
              assessment_ids: formData.assessment_ids,
              include_skills_analysis: formData.include_skills_analysis,
              include_performance_metrics: formData.include_performance_metrics,
              include_recommendations: formData.include_recommendations
            } : null,
            prescreening_data: formData.session_id ? {
              session_id: formData.session_id,
              include_question_analysis: formData.include_question_analysis,
              include_response_patterns: formData.include_response_patterns,
              include_communication_style: formData.include_communication_style
            } : null,
            include_overall_score: formData.include_overall_score,
            include_strengths_weaknesses: formData.include_strengths_weaknesses,
            include_development_areas: formData.include_development_areas,
            include_career_recommendations: formData.include_career_recommendations,
            include_team_fit_analysis: formData.include_team_fit_analysis,
            target_role: formData.target_role || null,
            target_company: formData.target_company || null
          });
          break;
        
        default:
          throw new Error('Invalid report type');
      }

      // Toast message is already handled for assessment reports above
      if (reportType !== 'assessment') {
        toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generation started!`);
      }
      
      if (onReportGenerated) {
        // For assessment reports, pass the array of reports; for others, pass the single report
        onReportGenerated(Array.isArray(response.data) ? response.data : response.data);
      }
      
      onClose();
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getReportTypeDescription = (type) => {
    switch (type) {
      case 'assessment':
        return 'Analyzes skill assessment results including technical skills, performance metrics, and development recommendations.';
      case 'prescreening':
        return 'Interprets prescreening responses including communication style, cultural fit indicators, and professional maturity.';
      case 'impression':
        return 'Comprehensive analysis combining assessment and prescreening data for overall candidate evaluation.';
      default:
        return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Generate Report</h2>
            <p className="text-gray-600">Create an AI-powered report for this candidate</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6 h-full flex flex-col">
            {/* Report Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Report Type</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { value: 'assessment', label: 'Assessment Report', icon: '📊' },
                  { value: 'prescreening', label: 'Prescreening Report', icon: '❓' },
                  { value: 'impression', label: 'Impression Report', icon: '🎯' }
                ].map((type) => (
                  <div
                    key={type.value}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      reportType === type.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setReportType(type.value)}
                  >
                    <div className="text-center">
                      <div className="text-3xl mb-2">{type.icon}</div>
                      <div className="font-medium text-gray-900">{type.label}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {getReportTypeDescription(type.value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Assessment Selection - Only for Impression Reports */}
            {reportType === 'impression' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Assessments (Optional)
                </label>
                {!Array.isArray(availableAssessments) || availableAssessments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📊</div>
                    <p>No assessments found for this candidate</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {availableAssessments.map((assessment) => (
                      <label key={assessment.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.assessment_ids.includes(assessment.id)}
                          onChange={() => handleAssessmentToggle(assessment.id)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900">{assessment.skill_name || assessment.title || 'Assessment'}</div>
                          <div className="text-sm text-gray-500">
                            Completed: {assessment.completed_at ? new Date(assessment.completed_at).toLocaleDateString() : 'Unknown'}
                            {assessment.score && ` • Score: ${assessment.score}%`}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Show info for assessment reports - all assessments will be included */}
            {reportType === 'assessment' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Assessments Included
                </label>
                {!Array.isArray(availableAssessments) || availableAssessments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="text-4xl mb-2">📊</div>
                    <p>No completed assessments found for this candidate</p>
                    <p className="text-xs mt-2">Complete an assessment first to generate a report</p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <div className="font-medium text-blue-900">All Completed Assessments Will Be Included</div>
                        <div className="text-sm text-blue-700 mt-2">
                          <div className="space-y-1">
                            {availableAssessments.map((assessment) => (
                              <div key={assessment.id}>
                                • {assessment.skill_name || assessment.title || 'Assessment'}
                                {assessment.score && ` (Score: ${assessment.score}%)`}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Prescreening Session Selection - Only show if multiple sessions exist */}
            {((reportType === 'prescreening' || reportType === 'impression') && availableSessions.length > 1) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Prescreening Session {reportType === 'impression' && '(Optional)'}
                </label>
                <div className="space-y-2">
                  {availableSessions.map((session) => (
                    <label key={session.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="session_id"
                        value={session.id}
                        checked={formData.session_id === session.id}
                        onChange={(e) => handleInputChange('session_id', e.target.value)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">
                          {new Date(session.startedAt).toLocaleDateString()} - {new Date(session.startedAt).toLocaleTimeString()}
                        </div>
                        <div className="text-sm text-gray-500">
                          {session.answeredQuestions}/{session.totalQuestions} questions answered
                          {session.overallScore && ` • Score: ${session.overallScore}%`}
                          {session.isPassed !== undefined && (
                            <span className={session.isPassed ? 'text-green-600 ml-1' : 'text-red-600 ml-1'}>
                              {session.isPassed ? '(Passed)' : '(Not Passed)'}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* No completed prescreening sessions */}
            {reportType === 'prescreening' && availableSessions.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">❓</div>
                <p>No completed prescreening found for this candidate</p>
                <p className="text-xs mt-2">
                  The candidate must complete prescreening before a prescreening report can be generated.
                </p>
              </div>
            )}

            {/* Show info for single session in prescreening reports */}
            {(reportType === 'prescreening' && availableSessions.length === 1) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <div className="font-medium text-blue-900">Using Most Recent Session</div>
                    <div className="text-sm text-blue-700 mt-1">
                      Report will be generated from: {new Date(availableSessions[0].startedAt).toLocaleString()} 
                      {availableSessions[0].overallScore && ` (Score: ${availableSessions[0].overallScore}%)`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Report Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Report Options</label>
              <div className="space-y-3">
                {reportType === 'assessment' && (
                  <>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_skills_analysis}
                        onChange={(e) => handleInputChange('include_skills_analysis', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include skills analysis</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_performance_metrics}
                        onChange={(e) => handleInputChange('include_performance_metrics', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include performance metrics</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_recommendations}
                        onChange={(e) => handleInputChange('include_recommendations', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include recommendations</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_benchmarking}
                        onChange={(e) => handleInputChange('include_benchmarking', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include benchmarking data</span>
                    </label>
                  </>
                )}

                {reportType === 'prescreening' && (
                  <>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_question_analysis}
                        onChange={(e) => handleInputChange('include_question_analysis', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include question analysis</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_response_patterns}
                        onChange={(e) => handleInputChange('include_response_patterns', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include response patterns</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_communication_style}
                        onChange={(e) => handleInputChange('include_communication_style', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include communication style analysis</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_cultural_fit}
                        onChange={(e) => handleInputChange('include_cultural_fit', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include cultural fit analysis</span>
                    </label>
                  </>
                )}

                {reportType === 'impression' && (
                  <>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_overall_score}
                        onChange={(e) => handleInputChange('include_overall_score', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include overall score</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_strengths_weaknesses}
                        onChange={(e) => handleInputChange('include_strengths_weaknesses', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include strengths and weaknesses</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_development_areas}
                        onChange={(e) => handleInputChange('include_development_areas', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include development areas</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_career_recommendations}
                        onChange={(e) => handleInputChange('include_career_recommendations', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include career recommendations</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.include_team_fit_analysis}
                        onChange={(e) => handleInputChange('include_team_fit_analysis', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Include team fit analysis</span>
                    </label>
                  </>
                )}
              </div>
            </div>

            {/* Target Role and Company (for impression reports) */}
            {reportType === 'impression' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Role (Optional)</label>
                  <input
                    type="text"
                    value={formData.target_role}
                    onChange={(e) => handleInputChange('target_role', e.target.value)}
                    placeholder="e.g., Senior Developer"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Company (Optional)</label>
                  <input
                    type="text"
                    value={formData.target_company}
                    onChange={(e) => handleInputChange('target_company', e.target.value)}
                    placeholder="e.g., TechCorp Inc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  loading ||
                  (reportType === 'assessment' && availableAssessments.length === 0) ||
                  (reportType === 'prescreening' && availableSessions.length === 0)
                }
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportGeneration;
